import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { INTRO_SLIDES } from './introSlides';
import workitectLogo from '@/assets/workitect-full-logo.png';

interface IntroSlideshowProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Fullscreen click-through intro shown to every new user on first sign-in.
 * Highlights the differentiator concepts vs. competitor PM tools.
 * Keyboard: → / Enter = next, ← = prev, Esc = skip.
 */
export function IntroSlideshow({ open, onClose }: IntroSlideshowProps) {
  const [index, setIndex] = useState(0);
  const total = INTRO_SLIDES.length;
  const slide = INTRO_SLIDES[index];
  const isLast = index === total - 1;

  const next = useCallback(() => {
    if (isLast) { onClose(); return; }
    setIndex(i => Math.min(i + 1, total - 1));
  }, [isLast, onClose, total]);

  const prev = useCallback(() => {
    setIndex(i => Math.max(i - 1, 0));
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, next, prev, onClose]);

  if (!open) return null;

  const overlay = (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-background/95 backdrop-blur-sm p-4">
      {/* Skip */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
        aria-label="Skip intro"
      >
        Skip <X className="h-3.5 w-3.5" />
      </button>

      {/* Logo */}
      <img
        src={workitectLogo}
        alt="Workitect"
        className="absolute top-4 left-4 h-6 opacity-60"
      />

      <div
        key={index}
        className="w-full max-w-4xl animate-in fade-in slide-in-from-bottom-4 duration-300"
      >
        <div className="grid md:grid-cols-2 gap-6 md:gap-10 items-center rounded-2xl border border-border bg-card shadow-xl p-6 md:p-10">
          {/* Illustration */}
          <div className="order-first md:order-none">
            <div className="rounded-xl overflow-hidden bg-muted/40 aspect-[4/3]">
              <img
                src={slide.image}
                alt={slide.imageAlt}
                width={1024}
                height={768}
                loading={index === 0 ? 'eager' : 'lazy'}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Copy */}
          <div className="space-y-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
              {slide.kicker}
            </p>
            <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground leading-tight">
              {slide.title}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
              {slide.body}
            </p>
            <ul className="space-y-2 pt-1">
              {slide.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-foreground/85">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  <span className="leading-snug">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-6 flex items-center justify-between px-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={prev}
            disabled={index === 0}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>

          <div className="flex items-center gap-1.5" role="tablist" aria-label="Slide progress">
            {INTRO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index ? 'true' : undefined}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === index ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50'
                )}
              />
            ))}
          </div>

          <Button size="sm" onClick={next} className="gap-1">
            {isLast ? 'Get started' : 'Next'}
            {!isLast && <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>

        <p className="mt-3 text-center text-[11px] text-muted-foreground/70">
          {index + 1} of {total}
        </p>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
